/**
 * Browser-friendly entrypoint for the IIFE bundle.
 *
 * tsup's `globalName: 'ProxyScrape'` exposes the entire module namespace as
 * `window.ProxyScrape`, so users would otherwise have to write
 * `new ProxyScrape.ProxyScrape()`. We re-export the class as the *default*
 * export, then in the IIFE wrapper esbuild generates we attach the named
 * helpers to the class itself — giving callers:
 *
 *   const ps = new ProxyScrape();                // works
 *   if (e instanceof ProxyScrape.AuthError) ...  // works
 *
 * The Node/bundler ESM + CJS entries (src/index.ts) are unaffected.
 */
import { ProxyScrape } from './client';
import {
  AuthError,
  BadRequestError,
  NetworkError,
  ProxyScrapeError,
  RateLimitError,
} from './core/errors';

// Attach the error classes as static properties of the main class so
// `instanceof` checks work without a separate import on the global object.
// We avoid the obvious `Object.assign` cast — explicitly setting each is
// less terse but lets TypeScript type-check the additions.
type ProxyScrapeStatic = typeof ProxyScrape & {
  AuthError: typeof AuthError;
  BadRequestError: typeof BadRequestError;
  NetworkError: typeof NetworkError;
  ProxyScrapeError: typeof ProxyScrapeError;
  RateLimitError: typeof RateLimitError;
};

const ProxyScrapeBrowser = ProxyScrape as ProxyScrapeStatic;
ProxyScrapeBrowser.AuthError = AuthError;
ProxyScrapeBrowser.BadRequestError = BadRequestError;
ProxyScrapeBrowser.NetworkError = NetworkError;
ProxyScrapeBrowser.ProxyScrapeError = ProxyScrapeError;
ProxyScrapeBrowser.RateLimitError = RateLimitError;

// In the IIFE bundle, every export below becomes a property of
// `window.ProxyScrape`. The class is exported as `ProxyScrape` (so
// `new ProxyScrape.ProxyScrape()` works) and the error classes are
// exported individually so `instanceof window.ProxyScrape.AuthError`
// works without traversing the static-prop path.
export default ProxyScrapeBrowser;
export { ProxyScrapeBrowser as ProxyScrape };
export { AuthError, BadRequestError, NetworkError, ProxyScrapeError, RateLimitError };
